import { builder, BuilderContent, Builder } from '@builder.io/react';
import Error from 'next/error';
import NoSSR from 'react-no-ssr';
import { useRouter } from 'next/router';

import { unified } from 'unified';
import rehypeParse from 'rehype-parse';
import rehypeRemark from 'rehype-remark';
import remarkStringify from 'remark-stringify';
import { toHtml } from 'hast-util-to-html';

import { TwitterShareButton, FacebookShareButton, LinkedinShareButton } from 'react-share';

import Header from 'react-scale/components/ui/header';
import Article from 'react-scale/components/pages/blog/detail/article';
import Footer from 'react-scale/components/ui/footer';
import TryBanner from 'react-scale/components/sections/cta/simple-centered';
import Navigation from 'react-scale/components/ui/navigation';

import IconTwitter from 'react-scale/components/icons/share/Twitter';
import IconFacebook from 'react-scale/components/icons/share/Facebook';
import IconLinkedin from 'react-scale/components/icons/share/Linkedin';

import { themeDark } from 'react-scale/components/ui/header/header-themes';
import handleError from 'react-scale/lib/handle-error';

export default function Post({ blog }) {
  const router = useRouter();
  const isPreview =
    router.query.url === '/__builder_editing__' || Builder.isPreviewing || Builder.isEditing;

  if (!blog || (blog.published === 'draft' && !isPreview)) {
    return (
      <NoSSR>
        <Error statusCode={404} />
      </NoSSR>
    );
  }

  return (
    <BuilderContent
      content={blog}
      model="blog-model"
      options={{ includeRefs: true, includeUnpublished: true }}
    >
      {data => (
        <div className="text-white bg-black">
          <Header
            pathname={`/blog/${data?.slug}`}
            title={`${data?.title} - Scale`}
            description={data?.metaDescription}
            canonical={`https://scale.com/blog/${data?.slug}`}
            twImg={data?.twitterCard}
            ogImg={data?.facebookCard}
            theme={themeDark}
            borderColor="border-white/10"
            border
          />
          <Navigation title="blog" href="/blog" color="text-white" bgTransparent sticky={false}>
            <div className="flex justify-end space-x-7">
              <TwitterShareButton url={`https://scale.com/blog/${data?.slug}`} title={data?.title}>
                <IconTwitter className="w-5 h-5 duration-300 ease-in-out fill-current transition-color hover:text-gray-300" />
              </TwitterShareButton>
              <FacebookShareButton url={`https://scale.com/blog/${data?.slug}`} title={data?.title}>
                <IconFacebook className="w-5 h-5 duration-300 ease-in-out fill-current transition-color hover:text-gray-300" />
              </FacebookShareButton>
              <LinkedinShareButton url={`https://scale.com/blog/${data?.slug}`} title={data?.title}>
                <IconLinkedin className="w-5 h-5 duration-300 ease-in-out fill-current transition-color hover:text-gray-300" />
              </LinkedinShareButton>
            </div>
          </Navigation>
          <Article post={data} content={data?.content} />
          <TryBanner border />
          <Footer
            textColor="text-neutral-400"
            textHoverColor="text-white"
            borderColor="border-neutral-800"
            border
          />
        </div>
      )}
    </BuilderContent>
  );
}

export async function getStaticPaths() {
  const pages = await builder.getAll('blog-model', {
    options: { noTargeting: true, includeUnpublished: true },
    omit: 'data.blocks',
    limit: 200
  });

  return {
    paths: pages.map(page => ({ params: { slug: `${page.data.slug}` } })),
    fallback: true
  };
}

export async function getStaticProps({ params }) {
  let blog;

  try {
    const builderBlog = await builder
      .get('blog-model', {
        options: { noTargeting: true, includeUnpublished: true },
        query: { 'data.slug': params.slug },
        limit: 200
      })
      .promise();
    blog = builderBlog;
  } catch (error) {
    handleError(error);
  }

  if (!blog) {
    return {
      notFound: true,
      revalidate: 1
    };
  }

  const file = await unified()
    .use(rehypeParse)
    .use(rehypeRemark, {
      handlers: {
        figure(h, node) {
          return h(node, 'html', toHtml(node));
        },
        div(h, node) {
          return h(node, 'html', toHtml(node));
        }
      }
    })
    .use(remarkStringify)
    .process(blog.data.content);

  return {
    props: {
      blog: {
        ...blog,
        data: {
          ...blog.data,
          content: file.value,
          twitterCard: blog.data.twitterCard || blog.data.twitterCardInput || null,
          facebookCard: blog.data.facebookCard || blog.data.facebookCardInput || null
        }
      }
    },
    revalidate: 1
  };
}
